import { useDeleteStudyTag } from '@hooks/deleteQueries';
import {
  useAddStudyTagOption,
  useUpdateStudyTagAttribute,
  useUpdateStudyTagOption,
} from '@hooks/postQueries';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useState } from 'react';

function TagLibrary({ tags, onDeleteTagValue, isUpdating, studyId }) {
  const [editingTag, setEditingTag] = useState(null);
  const [editingValue, setEditingValue] = useState(null);
  const [newValueInputs, setNewValueInputs] = useState({});
  const [editInputs, setEditInputs] = useState({});

  const { mutateAsync: addTagOption } = useAddStudyTagOption();
  const { mutateAsync: updateTagOption, isPending: isUpdatingTagOption } =
    useUpdateStudyTagOption();
  const { mutateAsync: updateTagAttribute, isPending: isUpdatingTagAttribute } =
    useUpdateStudyTagAttribute();
  const { mutateAsync: deleteStudyTag } = useDeleteStudyTag();
  const { mutateAsync: deleteStudyTagValue, isPending: isDeletingTagValue } = useDeleteStudyTag();

  // Handle adding a new tag value
  const handleAddValue = async (tagId) => {
    const value = newValueInputs[tagId]?.trim();
    if (!value) return;

    try {
      // Get the tag to find its name
      const tag = tags.find((t) => t.id === tagId);
      if (!tag) return;

      // Prepare payload with the tag's attribute name and new value
      const payload = {
        attribute: tag.tag_key, // Use the tag's name as the attribute
        values: [value], // The new tag value to add
      };

      await addTagOption({
        studyId,
        tagId,
        payload,
      });

      // Clear the input field
      setNewValueInputs((prev) => ({ ...prev, [tagId]: '' }));
    } catch (error) {
      console.error('Error adding tag value:', error);
    }
  };

  // Handle updating a tag name
  const handleUpdateTag = async (tagId) => {
    const newName = editInputs[tagId]?.trim();
    if (!newName) return;

    const tag = tags.find((t) => t.id === tagId);
    if (!tag) return;

    try {
      // Prepare payload with the updated tag name
      const payload = {
        attribute: newName, // Update the tag name
        values: tag.tag_values.map((v) => v.value), // Keep existing values
      };

      await updateTagAttribute({
        studyId,
        tagId,
        payload,
      });

      // Reset editing state
      setEditingTag(null);
    } catch (error) {
      console.error('Error updating tag:', error);
    }
  };

  // Handle updating a tag value
  const handleUpdateValue = async (tagId, valueId, newValue) => {
    const value = newValue?.trim();
    if (!value) return;

    const tag = tags.find((t) => t.id === tagId);
    if (!tag) return;

    try {
      // Find the value to update
      const valueToUpdate = tag.tag_values.find((v) => v.id === valueId);
      if (!valueToUpdate) return;

      // Prepare payload with updated value
      const payload = {
        attribute: value, // Keep the same tag name
        values: [''],
      };

      await updateTagOption({
        studyId,
        tagId,
        valueId,
        payload,
      });

      // Reset editing state
      setEditingValue(null);
    } catch (error) {
      console.error('Error updating tag value:', error);
    }
  };

  // Handle keyboard events
  const handleKeyDown = (e, handler) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handler();
    } else if (e.key === 'Escape') {
      setEditingTag(null);
      setEditingValue(null);
    }
  };

  // Show loading state
  if (isUpdating) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Show empty state
  if (!tags?.length) {
    return (
      <Box textAlign="center" p={3} color="text.secondary">
        <Typography>No tags found. Create your first tag to get started.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {tags.map((tag) => (
        <Box key={tag.id} mb={3}>
          {/* Tag Header */}
          <Box
            display="flex"
            alignItems="center"
            mb={1}
            sx={{
              p: 1,
              bgcolor: 'action.hover',
              borderRadius: 1,
            }}
          >
            {editingTag === tag.id ? (
              <Box display="flex" alignItems="center" flexGrow={1}>
                <TextField
                  size="small"
                  value={editInputs[tag.id] || tag.tag_key}
                  onChange={(e) => setEditInputs((prev) => ({ ...prev, [tag.id]: e.target.value }))}
                  onKeyDown={(e) => handleKeyDown(e, () => handleUpdateTag(tag.id))}
                  fullWidth
                  sx={{ mr: 1 }}
                />
                <IconButton size="small" onClick={() => handleUpdateTag(tag.id)} color="primary">
                  {isUpdatingTagAttribute ? <CircularProgress size={18} /> : <CheckIcon />}
                </IconButton>
                <IconButton size="small" onClick={() => setEditingTag(null)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            ) : (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, flexGrow: 1 }}>
                  {tag.tag_key}
                </Typography>
                <Tooltip title="Edit tag name">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditInputs((prev) => ({ ...prev, [tag.id]: tag.tag_key }));
                      setEditingTag(tag.id);
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete tag">
                  <IconButton
                    size="small"
                    onClick={() => deleteStudyTag({ id: studyId, tagId: tag.id })}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>

          {/* Tag Values */}
          <Box pl={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {tag.tag_values?.map((value) => (
                <Chip
                  key={value.id}
                  label={
                    editingValue === value.id ? (
                      <Box display="flex" alignItems="center">
                        <TextField
                          size="small"
                          value={editInputs[`${tag.id}-${value.id}`] || value.value}
                          onChange={(e) =>
                            setEditInputs((prev) => ({
                              ...prev,
                              [`${tag.id}-${value.id}`]: e.target.value,
                            }))
                          }
                          onKeyDown={(e) =>
                            handleKeyDown(e, () =>
                              handleUpdateValue(
                                tag.id,
                                value.id,
                                editInputs[`${tag.id}-${value.id}`],
                              ),
                            )
                          }
                          sx={{ width: 100 }}
                        />
                        <IconButton
                          size="small"
                          onClick={() =>
                            handleUpdateValue(tag.id, value.id, editInputs[`${tag.id}-${value.id}`])
                          }
                        >
                          {isUpdatingTagOption ? <CircularProgress size={16} /> : <CheckIcon />}
                        </IconButton>
                        <IconButton size="small" onClick={() => setEditingValue(null)}>
                          <CloseIcon />
                        </IconButton>
                      </Box>
                    ) : (
                      value.value
                    )
                  }
                  deleteIcon={
                    isDeletingTagValue ? (
                      <CircularProgress size={16} />
                    ) : (
                      <CloseIcon sx={{ fontSize: 10 }} />
                    )
                  }
                  onDelete={
                    onDeleteTagValue
                      ? () => deleteStudyTagValue({ id: studyId, tagId: tag.id, valueId: value.id })
                      : null
                  }
                  onClick={() => {
                    setEditInputs((prev) => ({
                      ...prev,
                      [`${tag.id}-${value.id}`]: value.value,
                    }));
                    setEditingValue(value.id);
                  }}
                  sx={{
                    maxWidth: 200,
                    borderRadius: 1,
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              ))}

              {/* Add new value input */}
              <Box display="flex" alignItems="center" gap={1} pl={1}>
                <TextField
                  size="small"
                  placeholder="Add option"
                  value={newValueInputs[tag.id] || ''}
                  onChange={(e) =>
                    setNewValueInputs((prev) => ({ ...prev, [tag.id]: e.target.value }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleAddValue(tag.id);
                    }
                  }}
                  sx={{ width: 120 }}
                />
                <IconButton
                  size="small"
                  onClick={() => handleAddValue(tag.id)}
                  disabled={!newValueInputs[tag.id]?.trim()}
                >
                  <AddIcon />
                </IconButton>
              </Box>
            </Stack>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default TagLibrary;
